import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Eye, Trash2, Lock } from "lucide-react";
import { toast } from "sonner";
import { utc } from "./syncText";

function DiagRow({ d, onDeleted }) {
  const [code, setCode] = useState("");
  const [shown, setShown] = useState(null);
  const [busy, setBusy] = useState(false);
  const reveal = async () => {
    setBusy(true);
    try {
      const { data: s } = await api.post("/auth/broker/diag-step-up", { totp_code: code });
      const { data } = await api.post(`/brokers/sharekhan/diagnostics/${d.id}/reveal`, { step_up_token: s.step_up_token });
      setShown(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setCode(""); setBusy(false);
    }
  };
  const remove = async () => {
    try { await api.delete(`/brokers/sharekhan/diagnostics/${d.id}`); setShown(null); onDeleted(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div data-testid={`diag-row-${d.id}`} className="rounded border border-[var(--border-subtle)] p-2 space-y-1">
      <div className="flex flex-wrap items-center gap-2 text-[11px] text-slate-400">
        <span className="font-mono text-slate-200">{d.kind}</span><span>{d.error}</span>
        <span>captured {utc(d.created_at)}</span><span>auto-deletes {utc(d.expires_at)}</span>
      </div>
      {shown ? (
        <div data-testid={`diag-revealed-${d.id}`} className="font-mono text-[11px] text-amber-200 break-all">
          {shown.readable ? Object.entries(shown.masked).map(([k, v]) => <div key={k}>{k}: “{v}”</div>) : "Unreadable (key changed or record tampered)"}
          <div className="mt-1 flex gap-3">
            <button className="text-xs text-slate-300 underline underline-offset-2 hover:text-white" onClick={() => setShown(null)} data-testid={`diag-hide-${d.id}`}>Hide</button>
            <button className="text-xs text-rose-300 underline underline-offset-2 hover:text-rose-200" onClick={remove} data-testid={`diag-delete-revealed-${d.id}`}>Delete now</button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <Input data-testid={`diag-code-${d.id}`} inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="6-digit code" className="h-7 w-28 bg-[var(--bg-canvas)] border-[var(--border-subtle)] text-xs" />
          <Button size="sm" data-testid={`diag-reveal-${d.id}`} disabled={code.length !== 6 || busy} onClick={reveal} className="h-7 bg-amber-600 hover:bg-amber-500 text-white"><Eye className="w-3.5 h-3.5 mr-1" />Reveal</Button>
          <Button size="sm" variant="outline" data-testid={`diag-delete-${d.id}`} onClick={remove} className="h-7 border-[var(--border-subtle)]"><Trash2 className="w-3.5 h-3.5 mr-1" />Delete now</Button>
        </div>
      )}
    </div>
  );
}

export function DiagnosticsSection() {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ["sharekhan-diagnostics"],
    queryFn: async () => (await api.get("/brokers/sharekhan/diagnostics")).data,
    gcTime: 0,
  });
  if (!data?.items?.length) return null;
  return (
    <div data-testid="diag-section" className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 space-y-2">
      <div className="flex items-center gap-2 text-xs font-semibold text-amber-200"><Lock className="w-3.5 h-3.5" /> Encrypted Sharekhan diagnostics (owner-only)</div>
      <div className="text-[10px] text-slate-500">Short texts Sharekhan returned instead of data, masked (digits → #, identifiers removed). Revealing needs a fresh authenticator code. Auto-deleted after {data.retention_hours}h.</div>
      {data.items.map((d) => <DiagRow key={d.id} d={d} onDeleted={() => qc.invalidateQueries({ queryKey: ["sharekhan-diagnostics"] })} />)}
    </div>
  );
}
